<template>
  <!-- No v-if: the sunset notice has to reach every v1 page, not just the ~33
       that mount a <version-banner>. root-layout.vue already gates on `user`. -->
  <div
    class="deprecation-banner"
    :class="{ 'deprecation-banner--compact': isCompact }"
  >
    <div class="deprecation-banner__message">
      <span class="deprecation-banner__icon">
        <v-icon small>mdi-calendar-blank-outline</v-icon>
      </span>
      <span class="deprecation-banner__text">
        <strong class="deprecation-banner__headline">{{ headline }}</strong>
        <span v-if="!isCompact" class="deprecation-banner__sub">
          Your data is already in the new one.
        </span>
      </span>
      <span v-if="daysLeftLabel && !isCompact" class="deprecation-banner__chip">
        {{ daysLeftLabel }}
      </span>
    </div>
    <span v-if="error" class="deprecation-banner__error">{{ error }}</span>
    <v-spacer v-if="!isCompact" />
    <!-- Carries the current v1 path across so triage can see which page the
         user couldn't leave — that's the migration blocker we're hunting. -->
    <a
      v-if="canReportGap && !isCompact"
      :href="reportUrl"
      class="deprecation-banner__report"
      @click="openReport"
    >
      Tell us what's missing
    </a>
    <v-btn
      v-if="switchTarget"
      :disabled="isLoading"
      :loading="isLoading"
      :height="isCompact ? 34 : 28"
      depressed
      class="deprecation-banner__button"
      @click="handleSwitch"
    >
      {{ buttonText }}
      <v-icon small right>mdi-arrow-right</v-icon>
    </v-btn>
    <!-- Only for users whose v2 home doesn't hang on a module (admins, Boom
         Host / BAM tenants), so this only links across. It deliberately does NOT
         call move-all-modules: that endpoint replaces module_versions wholesale
         from a list missing reservation_page, pricing and
         accounting_charge_profiles, and a missing key falls back to v1 — it
         would strand people while looking like a migration. -->
    <a
      v-else
      :href="v2HomeUrl"
      class="deprecation-banner__button deprecation-banner__button--link"
    >
      {{ isCompact ? 'New version' : 'Open the new version' }}
      <v-icon small right>mdi-arrow-right</v-icon>
    </a>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import axios from '@/axios/config'
import { getV2BaseUrl } from '@/utils/v2-base-url'
import { activeVersionBanner } from '@/utils/version-banner-registry'
import { v2HomeTarget } from '@/utils/v2-home-target'

// 2026-09-28 (#28549): v1 is removed on this date. Single source for the
// headline and the days-left count, so they can never drift apart.
const REMOVAL_DATE = '2027-01-03'

// Everyone outside the tenant's staff: investors, external agents, and the
// server's CONTRACTOR_ROLES (user.rb).
const NON_STAFF_ROLES = [
  'investor',
  'agent',
  'contractor',
  'maintenance',
  'cleaner',
  'cleaning-supervisor',
]

// Intercom renders the open messenger into one of these; used to tell "opened"
// from "silently rejected".
const MESSENGER_FRAME =
  '.intercom-messenger-frame, iframe[name="intercom-messenger-frame"]'

export default {
  name: 'DeprecationBanner',

  data() {
    return {
      isLoading: false,
      error: null,
    }
  },

  computed: {
    ...mapState(['user']),
    ...mapGetters(['isInvestor', 'isContractor']),

    // Investors and contractors are the two external, non-staff roles, and the
    // codebase pairs them everywhere (VersionBanner.vue:44). Neither should be
    // routed to tenant-internal surfaces.
    isExternalRole() {
      return this.isInvestor || this.isContractor
    },

    // index.html's shim assigns window.Intercom unconditionally as a queueing
    // stub, so its presence proves nothing — root-layout.vue:315 skips boot for
    // investors, and a queued showNewMessage there would just be swallowed.
    // Mirror that gate so those users follow the href instead of dead-clicking.
    canUseIntercom() {
      return !this.isExternalRole && typeof window.Intercom === 'function'
    },

    // The link's fallback is /app/support, which lists the whole tenant's
    // support tickets behind nothing but a login (App.jsx:1218,
    // SupportPage.jsx:80-81, scoped only by `label: tenantSlug`). Investors
    // never get Intercom booted so they always land there; everyone else does,
    // but the 1.5s fallback still sends them there whenever the Messenger is
    // rejected or hidden. So no non-staff role gets the link. Until there's an
    // owner-safe report form they get the notice and the exit link, not this.
    canReportGap() {
      return !NON_STAFF_ROLES.includes(this.user?.role)
    },

    // The outermost <version-banner> on screen — see version-banner-registry.
    target() {
      return activeVersionBanner()
    },

    // Off a registered page, the role's v2 home — investors and contractors
    // never register a target (VersionBanner.vue), so this is their only way
    // out. Switching persists its module, else v2 sends them back.
    switchTarget() {
      return this.target || v2HomeTarget(this.user)
    },

    // Phones can't fit the full sentence plus the full label — at 390px the
    // banner wrapped to four lines. Shorten both rather than let it stack.
    isCompact() {
      return this.$vuetify.breakpoint.xsOnly
    },

    // The removal date is the point of the banner, so it survives into the
    // compact copy — only the sentence around it shortens.
    headline() {
      const date = this.$moment(REMOVAL_DATE)
      return this.isCompact
        ? `This version retires ${date.format('MMM D, YYYY')}`
        : `This version of Boom retires ${date.format('MMMM D, YYYY')}.`
    },

    daysLeft() {
      return this.$moment(REMOVAL_DATE).diff(
        this.$moment().startOf('day'),
        'days'
      )
    },

    daysLeftLabel() {
      if (this.daysLeft < 1) return null
      return `${this.daysLeft} ${this.daysLeft === 1 ? 'day' : 'days'} left`
    },

    buttonText() {
      if (this.isLoading) return 'Switching...'
      if (this.isCompact) return 'New version'
      return this.target ? 'Switch to the new version' : 'Open the new version'
    },

    // /app/ renders HomeRedirect, which lands each role via getHomeUrl — only
    // used where that is already v2 (see v2-home-target.js). NOT
    // /app/dashboard/system (view-switcher.vue's fallback): that page hard-
    // returns "Access Restricted" for anything but admin/system_admin
    // (SystemDashboard.jsx:614), and view-switcher never had to serve
    // investors because root-layout.vue hides it from them. This banner does.
    v2HomeUrl() {
      return `${getV2BaseUrl()}/app/`
    },

    // $route, not window.location: this banner is mounted once in the
    // persistent root layout and v1 navigates client-side, so a computed
    // reading window.location.pathname has no reactive dep and would stay
    // cached on the session's first page — reporting the wrong page forever.
    currentPath() {
      return this.$route.fullPath
    },

    // Seeds the Intercom composer. Intercom has no API to open a ticket *form*,
    // so the label rides in the message body instead of a field.
    reportPrefill() {
      return `[v2 missing feature] I am missing the following feature in v2: \n\nI was using this v1 page: ${this.currentPath}`
    },

    // Fallback target only — see openReport(). Intercom is not booted for
    // investors (root-layout.vue:315), nor when a tenant sets hide_intercom,
    // nor on mobile/agents; those users get v2's ticket form rather than a
    // dead click.
    reportUrl() {
      const from = encodeURIComponent(this.currentPath)
      return `${getV2BaseUrl()}/app/support?report=v2-missing-feature&from=${from}`
    },
  },

  mounted() {
    this.publishHeight()
  },

  updated() {
    this.publishHeight()
  },

  beforeDestroy() {
    this.setBannerHeight(0)
  },

  methods: {
    // side-menu.vue's fixed drawer is offset by the app bar alone, so it would
    // start underneath this banner and cover it. Publish the height so it can
    // sit below us instead.
    publishHeight() {
      this.$nextTick(() => {
        this.setBannerHeight(this.$el ? this.$el.offsetHeight || 0 : 0)
      })
    },

    setBannerHeight(px) {
      document.documentElement.style.setProperty(
        '--v1-banner-height',
        `${px}px`
      )
    },

    // Progressive enhancement: the anchor's href is the fallback, and we upgrade
    // to the Intercom composer whenever the Messenger is actually on the page.
    openReport(event) {
      if (!this.canUseIntercom) return
      event.preventDefault()
      window.Intercom('showNewMessage', this.reportPrefill)

      // showNewMessage is fire-and-forget: when Intercom rejects the session
      // (Messenger Security needs intercom_user_jwt, which application_
      // controller.rb:3777 only issues when INTERCOM_APP_ID is set) the call is
      // swallowed and the user gets a dead click. If no messenger frame has
      // appeared, fall through to v2's ticket form.
      setTimeout(() => {
        if (!document.querySelector(MESSENGER_FRAME)) {
          window.location.href = this.reportUrl
        }
      }, 1500)
    },

    async handleSwitch() {
      // The button is v-if'd on switchTarget, but the banner can render without
      // one — never let a stray call reach switchTarget.v2Url.
      if (!this.switchTarget) return

      if (!this.user?.id) {
        this.error = 'User not authenticated'
        return
      }

      this.isLoading = true
      this.error = null

      try {
        await this.persistModuleVersion()
        window.location.href = `${getV2BaseUrl()}${this.switchTarget.v2Url}`
      } catch (err) {
        console.error('Error switching version:', err)
        this.error = err.message || 'Failed to switch version'
        this.isLoading = false
      }
    },

    async persistModuleVersion() {
      const updatedModuleVersions = {
        ...(this.user.module_versions || {}),
        [this.switchTarget.moduleKey]: { module_version: 2 },
      }

      await axios.post(`/api/users/${this.user.id}/module-versions`, {
        module_versions: updatedModuleVersions,
      })

      this.$store.commit('UPDATE_USER', {
        ...this.user,
        module_versions: updatedModuleVersions,
      })
    },
  },
}
</script>

<style scoped>
/* Red: v1 is being switched off, so this has to read as urgent — but stay a
   single dense strip. White on #b42318 is ~6.5:1. */
.deprecation-banner {
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 40px;
  padding: 4px 16px;
  box-sizing: border-box;
  background: #fdeeec;
  border-bottom: 1px solid #f2b8b0;
  /* Above side-menu.vue's permanent drawer (z-index 6), whose full-height box
     would otherwise cover our left edge. Its content is offset below us by
     --v1-banner-height, so we only paint over its empty padding. Stays below
     the temporary nav drawer (z-index 1000). */
  position: relative;
  z-index: 7;
}

.deprecation-banner__message {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.deprecation-banner__icon {
  display: flex;
  flex-shrink: 0;
}

.deprecation-banner__icon .v-icon {
  color: #b42318 !important;
}

.deprecation-banner__text {
  display: flex;
  align-items: baseline;
  gap: 8px;
  min-width: 0;
  font-size: 13px;
}

.deprecation-banner__headline {
  font-weight: 700;
  color: #5f140c;
  white-space: nowrap;
}

.deprecation-banner__sub {
  font-weight: 500;
  color: #7a2a20;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.deprecation-banner__chip {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  height: 20px;
  padding: 0 8px;
  border-radius: 10px;
  background: #ffffff;
  border: 1px solid #f2b8b0;
  color: #9b1c12;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
}

.deprecation-banner__report {
  font-size: 12px;
  font-weight: 500;
  color: #9b1c12 !important;
  text-decoration: underline;
  text-underline-offset: 2px;
  white-space: nowrap;
}

.deprecation-banner__report:hover {
  color: #5f140c !important;
}

.deprecation-banner__button {
  display: inline-flex;
  align-items: center;
  height: 28px;
  padding: 0 12px !important;
  border-radius: 6px !important;
  background: #b42318 !important;
  color: #ffffff !important;
  font-size: 12px !important;
  font-weight: 600;
  letter-spacing: normal;
  text-transform: none;
  text-decoration: none;
  white-space: nowrap;
}

.deprecation-banner__button:hover {
  background: #8f1c13 !important;
}

.deprecation-banner__button .v-icon {
  color: #ffffff !important;
}

/* Below ~1100px the supporting line is the first thing to give way. */
@media (max-width: 1100px) {
  .deprecation-banner__sub {
    display: none;
  }
}

/* Phone: headline left (wraps if it must), button right. The report link is
   desktop-only. */
.deprecation-banner--compact {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  column-gap: 12px;
  row-gap: 2px;
  padding: 8px 12px;
}

.deprecation-banner--compact .deprecation-banner__message {
  grid-column: 1;
  grid-row: 1;
  align-self: center;
}

.deprecation-banner--compact .deprecation-banner__headline {
  white-space: normal;
  line-height: 1.3;
}

.deprecation-banner--compact .deprecation-banner__button {
  grid-column: 2;
  grid-row: 1;
  justify-content: center;
  height: 34px;
  font-size: 13px !important;
}

.deprecation-banner--compact .deprecation-banner__error {
  grid-column: 1 / -1;
}

.deprecation-banner__error {
  font-size: 11px;
  color: #b91c1c;
  font-weight: 500;
}
</style>
